import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import {
  APOfferVacancyMeta,
  APOfferVacancyStatus,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import RentalAgreementUtils from "../../../vacancy-applicant/utils/VacancyRentalAgreementUtils";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import ModalManager from "@/components/ModalComponent/ModalManager";
import CBRentalAgreementFormWizard from "@/apps/tatar/cashBudget/views/tenants/components/rental-agreements/rental-agreement-form-wizard/CBRentalAgreementFormWizard";
import ObjectKindStruct from "@/redux/actions/struct/implemented/ObjectKindStruct";
import i18n from "@/i18n";
import DataBusDefaults from "@/services/DataBusDefaults";
import { CBRentalAgreementFormWizardStep2Value } from "@/apps/tatar/cashBudget/views/tenants/components/rental-agreements/rental-agreement-form-wizard/CBRentalAgreementFormWizardStep2ContractPartners";
import ContactForm from "@/modules/contacts-module/ContactForm";
import { Contact, PersonContact } from "@/model/db/Contact";
import { CBRentalAgreementFormWizardStep3Value } from "@/apps/tatar/cashBudget/views/tenants/components/rental-agreements/rental-agreement-form-wizard/CBRentalAgreementFormWizardStep3RentalUnits";
import { AssetTypes } from "@/model/AssetTypes";
import { CBRentalAgreementFormWizardStep4Value } from "@/apps/tatar/cashBudget/views/tenants/components/rental-agreements/rental-agreement-form-wizard/CBRentalAgreementFormWizardStep4ContractData";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import CacheService from "@/services/CacheService";
import { useActivityConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import { ActivityAbstractStructClass } from "@/redux/actions/struct/implemented/ActivityAbstractStruct";
import { APStatusTags } from "@/apps/AppConfigInterfaces";
import { HTTP } from "@/utils/Http";
import VacancyRentalAgreementUtils from "../../../vacancy-applicant/utils/VacancyRentalAgreementUtils";
import { RentalAgreement } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";

type Props = {
  activity: APActivity;
  offer: APOffer;
  activityStruct: ActivityAbstractStructClass<any>;
  reload: () => void;
};
const useVacancyRentalAgreement = (props: Props) => {
  const { activity, offer, activityStruct, reload } = props;
  const constants = useActivityConstants();

  const createRentalAgreement = async (application: VacancyApplication) => {
    createContact();
  };

  const createContact = async () => {
    const vacancyOfferMeta = offer.data.meta as APOfferVacancyMeta;
    const intialContactData =
      await RentalAgreementUtils.createRentalAgreementContacts(
        activity,
        vacancyOfferMeta.contractData
      );

    ModalManager.show({
      noPadding: true,
      backdrop: "static",
      maxWidth: 1600,
      size: "md",
      content: (states, setStates, closeContactModal) => (
        <ContactForm
          contactTypes={["TENANT"]}
          onClose={closeContactModal}
          businessUnits={[activity.data.type]}
          onSuccessfulSubmit={(contact) => {
            closeContactModal();
            setTimeout(() => {
              openRentalAgreementWizzard(contact as PersonContact);
            }, 500);
          }}
          contact={{ data: intialContactData } as unknown as Contact}
        />
      ),
    });
  };

  const openRentalAgreementWizzard = async (contact: PersonContact) => {
    const vacancyOfferMeta = offer.data.meta as APOfferVacancyMeta;
    const entity = OrgaStruct.getEntity(activity.data.entity);
    const entityObject = entity.objects.find(
      (o) => o._id === activity.data.objectId
    );

    if (!entityObject) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.CreateContacts.NoObject",
          "Beim Erstellen der Kontakte ist ein Fehler aufgetreten, das Objekt konnte nicht gefunden werden."
        ),
        duration: 3000,
      });
      return;
    }

    const objectKind = ObjectKindStruct.getKind(entityObject.objectKindId);
    const formAttachment =
      await VacancyRentalAgreementUtils.getContractAttachment(offer);

    const additionalAttachments =
      await VacancyRentalAgreementUtils.getAdditionalAttachments(offer);

    const object = (await CacheService.getData({
      id: activity.data.objectId,
      oType: "asset",
      assetType: AssetTypes.Portfolio.Object,
    })) as OAObject;

    const mainAddress =
      contact.data.address.find((a) => a.isMain) || contact.data.address[0];
    const contactEmails = contact.data.emails || [];
    const contactPhone = contact.data.phone || [];

    const step2InitialValues: CBRentalAgreementFormWizardStep2Value = {
      entity: entity._id,
      objectId: activity.data.objectId,
      tenant: contact._id,
      contactOption: {
        addressId: mainAddress?.id || "",
        mail: contactEmails.map((e) => {
          return e.id;
        }),
        phone: contactPhone.map((e) => {
          return e.id;
        }),
        contactPersonId:
          contact.data.contactPersons && contact.data.contactPersons[0]
            ? contact.data.contactPersons[0].id
            : null,
        sms: contactPhone.map((e) => {
          return e.id;
        }),
      },
    };

    const step3InitialValues: CBRentalAgreementFormWizardStep3Value = {
      rentalUnits: activity.data.relations.filter(
        (r) => r.assetType === AssetTypes.Rental.RentalUnit
      ),
    };

    const rentId = getDebitpositionId(object, "rent");
    const operatingCostId = getDebitpositionId(object, "operatingCost");
    const depositId = getDepositId(object);

    const step4InitialValues: CBRentalAgreementFormWizardStep4Value = {
      rentPaymentTimingOptions: "startOfMonth",
      deposit: [
        {
          id: depositId,
          depositValue: vacancyOfferMeta.contractData.depositSum,
          depositStart: vacancyOfferMeta.contractData.depositDate,
        },
      ],
      taxable: "notTaxable",
      paymentPositions: [
        {
          id: rentId,
          net: vacancyOfferMeta.contractData.rentalUnitRentBase,
          gross: vacancyOfferMeta.contractData.rentalUnitRentBase,
        },
        {
          id: operatingCostId,
          net: vacancyOfferMeta.contractData.rentalUnitAdditionalCosts,
          gross: vacancyOfferMeta.contractData.rentalUnitAdditionalCosts,
        },
      ],
      contractType: "private",
      moveIn: vacancyOfferMeta.contractData.moveInDate,
      displayName: `${contact.data.displayName}`,
      automatic: null,
      copayment: null,
      graduatedRent: null,
      index: null,
      note: null,
      option: null,
      agreementExpiration: null,
      marks: null,
      moveOut: null,
      id: undefined,
    };

    ModalManager.show({
      ignoreEscapeToClose: true,
      title: "Mietvertrag erstellen",
      backdrop: "static",
      size: "fluid",
      noPadding: true,
      closable: false,
      content: (state, setState, onClose) => (
        <CBRentalAgreementFormWizard
          kind={objectKind}
          onClose={() => {
            onClose();
          }}
          step1InitialValues={{
            rentalAgreement: [formAttachment],
          }}
          step2InitialValues={step2InitialValues}
          step3InitialValues={step3InitialValues}
          step4InitialValues={step4InitialValues}
          step5InitialValues={{
            additionalAttachments: additionalAttachments,
          }}
          onSuccess={(agreement: RentalAgreement) => {
            onRentalAgreementCreationSuccess(agreement);
          }}
        />
      ),
    });
  };

  const getDebitpositionId = (
    object: OAObject,
    type: "rent" | "operatingCost"
  ): string => {
    const immoFeature = object.data.feature?.immo;
    const id = immoFeature.accounting.debitposition
      .filter((position) => position.relevantForAgreement)
      .filter((position) => position.taxRate === 0)
      .filter((position) => position.status !== "archived")
      .find((p) => p.kind === type);

    return id.id || "";
  };

  const getDepositId = (object: OAObject): string => {
    const immoFeature = object.data.feature?.immo;
    const id = immoFeature.accounting.deposit.find(
      (d) => d.automaticDebitPosition && d.status !== "archived"
    );
    return id.id;
  };

  const onRentalAgreementCreationSuccess = async (
    agreement: RentalAgreement
  ) => {
    try {
      const finishedStatus = activityStruct.getStatusByTypeAndTags(
        activity.data.type,
        APStatusTags.FINISHED_STATUS
      );

      if (!finishedStatus || finishedStatus.length === 0) {
        DataBusDefaults.toast({
          type: "error",
          text: i18n.t(
            "apTemplate:Vacancy.Applicant.RentalAgreementCreation.FinishVacancy.NoFinishedStatus",
            "Es wurde kein abschliessender Status für die Leerstandsaktivitäten gefunden werden."
          ),
          duration: 3000,
        });
        return;
      }

      const newOfferStatus: APOfferVacancyStatus = "rental_agreement_created";

      await HTTP.post({
        url: `${constants.serviceUrl}/${offer._id}/updateOffer`,
        target: "EMPTY",
        bodyParams: {
          status: newOfferStatus,
        },
      });
      reload();

      await HTTP.post({
        url: `${constants.serviceUrl}/${activity._id}/setStatus`,
        target: "EMPTY",
        bodyParams: {
          newStatus: finishedStatus[0].id,
          newSubStatus: null,
        },
      });

      const result = await HTTP.post({
        url: `${constants.serviceUrl}/${activity._id}/setRelations`,
        target: "EMPTY",
        bodyParams: {
          relations: [
            ...(activity.data.relations || []),
            {
              assetType: AssetTypes.Rental.RentalAgreement,
              assetId: agreement._id,
            },
          ],
        },
      });
      CacheService.updateDataInCaches(activity._id, result);

      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.FinishVacancy.Success",
          "Der Leerstand wurde erfolgreich abgeschlossen."
        ),
        duration: 3000,
      });
    } catch (error) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.FinishVacancy.Error",
          "Beim Beenden der Leerstandsaktivität ist ein Fehler aufgetreten."
        ),
        duration: 3000,
      });
    }
  };

  return { createRentalAgreement };
};

export default useVacancyRentalAgreement;
